/**
 * BimSystemList.jsx — canonical BIM Coordination outline components (Supabase-backed;
 * migration database/111_bim.sql, service layer services/bim.js).
 *
 * One-Component-Per-Node-Type:
 *   BimRow           — BIM System row (expand → elements). Used by DesignSection (Reference >
 *                      Design > BIM Systems), MilestoneSections (under BIM-coordination
 *                      milestones — passes `restrictPhase` so only that milestone's own phase
 *                      shows; Reference leaves it unset and shows every phase).
 *   BimElementRow    — BIM Element row (expand → per-phase criteria sections).
 *   BimPhaseSection  — one phase's criteria checklist (SD/DD/CD/IA) + Mark Complete. Zoomable
 *                      (bim-phase-detail:<elementId>:<phase>) — the smallest zoomable unit;
 *                      criterion rows below it are deliberately leaves, no zoom.
 *   BimCriterionRow  — leaf checklist row: ⋯ menu (Complete/Mark incomplete) in the triangle
 *                      column — mirrors the Drawing Sheet checklist's content-less-row pattern
 *                      (DrawingSheetList.jsx SheetClItemNode) — + Markdown text + COORD chip.
 *                      No inline checkbox; toggled via the menu or the global 'x' shortcut
 *                      (data-cl-kind="bim-criterion" on its .wf-node, read by outline.js's 'x'
 *                      handler, which calls window.ajScToggleBimCriterion).
 *   BimSystemZoom / BimElementZoom / BimPhaseZoom — children-only renders for the three entity
 *                      zooms (bim-system-detail: / bim-element-detail: / bim-phase-detail:).
 *
 * Data flow: elements lazy-load per system through ctx.loader ('bimElements:<systemId>')
 * — promise-deduped, three-state (undefined/null/array). Element mutations update the
 * loader cache in place (updateElementInCache) so every mounted view re-renders from
 * one source. ctx.bimZoomCache[elementId] = { element, system } backs the deep-linked
 * element/phase zooms' breadcrumb/title resolution in the ES5 outline engine.
 */
import { useState, useEffect } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import Markdown from './Markdown.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import {
  BIM_PHASES, BIM_PHASE_LABELS, deriveSystemStatus,
  getBimSystemElements, getBimElement,
  toggleBimCriterion, markBimElementStarted, markBimPhaseComplete,
} from '../services/bim.js'

const PHASE_COLORS = { sd: '#d97706', dd: '#2563eb', cd: '#0d9488', ia: '#7c3aed' }

function statusColor(status) {
  if (!status) return '#7a9aaa'
  if (status.indexOf('Complete') !== -1) return '#1D9E75'
  if (status === 'Not Started' || status === 'No Elements') return '#9aa0a6'
  return '#1a6fa8'
}

/** Mirror of api/bim.js elementStatus — for optimistic client-side updates. */
function elementStatusLocal(el) {
  if (el.ia_complete) return 'IA Complete'
  if (el.cd_complete) return 'CD Complete'
  if (el.dd_complete) return 'DD Complete'
  if (el.sd_complete) return 'SD Complete'
  if (el.started) return 'Started'
  return 'Not Started'
}

function actorName() {
  const f = ctx.currentMember?.fields || {}
  return f['Name'] || f['Member Name'] || f['Full Name'] || ''
}

function loaderKey(systemId) { return 'bimElements:' + systemId }

/** Apply a mutation to one element inside the loader cache + zoom cache, re-render. */
function updateElementInCache(systemId, elementId, mutate) {
  const key = loaderKey(systemId)
  const els = ctx.loader.get(key)
  if (Array.isArray(els)) {
    ctx.loader.set(key, els.map(el => {
      if (el.id !== elementId) return el
      const next = { ...el }
      mutate(next)
      next.status = elementStatusLocal(next)
      return next
    }))
  }
  const z = ctx.bimZoomCache?.[elementId]
  if (z && z.element) {
    const next = { ...z.element }
    mutate(next)
    next.status = elementStatusLocal(next)
    ctx.bimZoomCache[elementId] = { ...z, element: next }
  }
  window.dispatchEvent(new Event('outline-render'))
}

/** Find an element wherever it's currently cached — the system's loader cache (mutations land
 *  there first) or the deep-link zoom cache — so criterion toggling works identically whether
 *  the element is expanded inline, zoomed into directly, or zoomed one level deeper (its phase). */
function findBimElement(systemId, elementId) {
  const els = ctx.loader.get(loaderKey(systemId))
  if (Array.isArray(els)) {
    const found = els.find(e => e.id === elementId)
    if (found) return found
  }
  const z = ctx.bimZoomCache?.[elementId]
  return (z && z.element) || null
}

/** Toggle one criterion's done state — the single code path shared by the criterion row's own
 *  ⋯ menu action and the global 'x' keyboard shortcut (window.ajScToggleBimCriterion below). */
async function toggleBimCriterionState(systemId, elementId, phase, criterionId) {
  const el = findBimElement(systemId, elementId)
  const criterion = el?.criteria?.[phase]?.find(c => c.id === criterionId)
  if (!criterion) return
  const next = !criterion.done
  const name = actorName()
  const memberId = wf.currentMemberId || null
  updateElementInCache(systemId, elementId, e => {
    e.criteria = { ...e.criteria, [phase]: e.criteria[phase].map(c =>
      c.id === criterionId ? {
        ...c, done: next,
        by_name: next ? name : null, by_airtable_id: next ? memberId : null,
        at: next ? new Date().toISOString() : null,
      } : c) }
  })
  try { await toggleBimCriterion(elementId, criterionId, next, memberId, name) }
  catch (err) {
    console.error('toggleCriterion failed', err)
    updateElementInCache(systemId, elementId, e => {
      e.criteria = { ...e.criteria, [phase]: e.criteria[phase].map(c =>
        c.id === criterionId ? { ...c, done: !next } : c) }
    })
  }
}

// 'x' keyboard shortcut bridge (outline.js) — mirrors window.ajScToggleSheetClItem's pattern for
// a React node with no ES5 menu button to read a data-fn from; reads data-cl-* attrs instead
// (see BimCriterionRow's .wf-node, data-cl-kind="bim-criterion").
if (typeof window !== 'undefined') window.ajScToggleBimCriterion = toggleBimCriterionState

/* Thin labeled progress bar — phase percent on the system row. */
function PhaseBar({ label, pct, color }) {
  if (pct === null || pct === undefined) return null
  const p = Math.max(0, Math.min(100, Math.round(pct)))
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color, letterSpacing: '0.04em', minWidth: 16, textAlign: 'right' }}>{label}</span>
      <div style={{ width: 44, height: 5, background: 'var(--aj-border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: p + '%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-mid)', minWidth: 28 }}>{p}%</span>
    </div>
  )
}

/* ── BimRow — canonical BIM System row ────────────────────────────────────── */

export function BimRow({ item, projectId, projectName, restrictPhase }) {
  const name   = item.name || item.fields?.['Name'] || 'BIM System'
  const status = item.stats ? deriveSystemStatus(item.stats) : (item.fields?.['Status'] || '')
  const color  = statusColor(status)
  const collapseKey = 'bim:' + item.id
  const isCollapsed = wf.collapsed[collapseKey] !== false

  const [showSlackPanel, setShowSlackPanel] = useState(false)
  const [localThreadTs,  setLocalThreadTs]  = useState(item.slack_thread_ts || item.fields?.['Slack Thread Timestamp'] || '')
  const menu = useActionMenu()

  const threadTs   = localThreadTs
  const lastReadTs = threadTs ? localStorage.getItem('slack:read:' + item.id) : null
  const hasUnread  = !!(threadTs && lastReadTs !== threadTs)

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[collapseKey] = !isCollapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleZoom() { ctx.zoomToSection('bim-system-detail:' + item.id, { root: 'design' }) }

  function handleAddElements() {
    window.dispatchEvent(new CustomEvent('open-add-bim-elements-modal', {
      detail: { systemId: item.id, systemTemplateId: item.system_template_id, systemName: name },
    }))
  }

  // Prefer live element data over the fetch-time stats once elements are loaded. Under a
  // milestone (restrictPhase set), only that milestone's own phase is relevant here — showing
  // DD/IA bars on an SD-coordination milestone's row is noise from a phase that hasn't started
  // yet in that context. Reference (restrictPhase null) still shows every phase.
  const els = ctx.loader.get(loaderKey(item.id))
  const barPhases = restrictPhase ? BIM_PHASES.filter(p => p === restrictPhase) : BIM_PHASES
  const phaseBars = barPhases.map(p => {
    let eligible = 0, complete = 0
    if (Array.isArray(els)) {
      for (const el of els) {
        if (!el.criteria || !el.criteria[p] || !el.criteria[p].length) continue
        eligible++
        if (el[p + '_complete']) complete++
      }
    } else if (item.stats && item.stats.phase && item.stats.phase[p]) {
      eligible = item.stats.phase[p].eligible
      complete = item.stats.phase[p].complete
    }
    return eligible > 0 ? { p, pct: Math.round((complete / eligible) * 100) } : null
  }).filter(Boolean)

  return (
    <div className={`wf-node wf-node--bim${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={collapseKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Focus on this system" data-kbzoom="1" onClick={handleZoom}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--bim">{name}</span>
          {status && <span className="wf-status-pill" style={{ color, borderColor: color + '33' }}>{status}</span>}
          {phaseBars.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
              {phaseBars.map(b => <PhaseBar key={b.p} label={BIM_PHASE_LABELS[b.p]} pct={b.pct} color={PHASE_COLORS[b.p]} />)}
            </span>
          )}
          {projectId && (
            <button
              className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
              title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
              onClick={e => { e.stopPropagation(); setShowSlackPanel(true) }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {hasUnread && <span className="wf-slack-unread-dot" />}
            </button>
          )}
        </div>
        {item.system_template_id && <ActionMenuButton menu={menu} />}
      </div>
      {item.system_template_id && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={handleAddElements}>Add elements</ActionMenuItem>
        </ActionMenu>
      )}
      {!isCollapsed && (
        <div className="wf-children">
          <BimSystemChildren systemId={item.id} restrictPhase={restrictPhase} />
        </div>
      )}
      {showSlackPanel && (
        <SlackThreadPanel
          entityId={item.id}
          entityType="bim"
          entityName={name}
          projectId={projectId}
          projectName={projectName || ''}
          threadTs={threadTs}
          onClose={() => setShowSlackPanel(false)}
          onThreadStarted={ts => setLocalThreadTs(ts)}
        />
      )}
    </div>
  )
}

/* ── Elements under a system (expanded children + system zoom content) ───────── */

function LoadingRow({ text }) {
  return (
    <div className="wf-node wf-node--item"><div className="wf-row">
      <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
      <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
      <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>{text}</span></div>
    </div></div>
  )
}

export function BimSystemChildren({ systemId, restrictPhase }) {
  // Only mounted while the system is expanded (or zoomed), so load on mount —
  // ctx.loader dedupes and re-dispatches outline-render on completion.
  useEffect(() => {
    ctx.loader.load(loaderKey(systemId), () => getBimSystemElements(systemId).then(d => {
      if (!ctx.bimElementSystem) ctx.bimElementSystem = {}
      for (const el of (d.elements || [])) ctx.bimElementSystem[el.id] = systemId
      return d.elements || []
    }))
  }, [systemId])

  const els = ctx.loader.get(loaderKey(systemId))
  if (!Array.isArray(els)) return <LoadingRow text="Loading elements..." />
  if (!els.length) return <LoadingRow text="No elements" />
  return <>{els.map(el => <BimElementRow key={el.id} element={el} systemId={systemId} restrictPhase={restrictPhase} />)}</>
}

/* ── BimElementRow — canonical BIM Element row ───────────────────────────────── */

export function BimElementRow({ element, systemId, restrictPhase }) {
  const collapseKey = 'bimElement:' + element.id
  const isCollapsed = wf.collapsed[collapseKey] !== false
  const menu = useActionMenu()
  const status = element.status || elementStatusLocal(element)
  const color = statusColor(status)
  const allPhasesWithCriteria = BIM_PHASES.filter(p => element.criteria?.[p]?.length)
  // Under a BIM-coordination milestone, only that milestone's own phase is relevant here —
  // Reference > Design > BIM Systems (restrictPhase unset) still shows every phase.
  const phasesWithCriteria = restrictPhase ? allPhasesWithCriteria.filter(p => p === restrictPhase) : allPhasesWithCriteria

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[collapseKey] = !isCollapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleZoom() { ctx.zoomToSection('bim-element-detail:' + element.id, { root: 'design' }) }

  async function handleMarkStarted() {
    updateElementInCache(systemId, element.id, el => {
      el.started = true; el.started_by_name = actorName(); el.started_at = new Date().toISOString()
    })
    try { await markBimElementStarted(element.id, wf.currentMemberId || null, actorName()) }
    catch (e) { console.error('markStarted failed', e) }
  }

  // Compact per-phase criteria counts, e.g. "SD 3/6 · DD 0/12".
  const counts = phasesWithCriteria.map(p => {
    const list = element.criteria[p]
    const done = list.filter(c => c.done).length
    return { p, done, total: list.length, complete: !!element[p + '_complete'] }
  })
  // Every phase shown here marked complete = this element is finished (Hide done hides it).
  const allComplete = counts.length > 0 && counts.every(c => c.complete)

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}${allComplete ? ' wf-node--is-done' : ''}`} data-key={collapseKey}>
      <div className="wf-row">
        {phasesWithCriteria.length ? (
          <button className="wf-triangle" onClick={toggleCollapse}>
            <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        )}
        <button className="wf-bullet wf-bullet--item" title="Focus on this element" data-kbzoom="1" onClick={handleZoom}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ flex: 1, minWidth: 0 }}>{element.name}</span>
          <span className="wf-meta">
            <span className="wf-status-pill" style={{ color, borderColor: color + '33' }}>{status}</span>
            {counts.map(c => (
              <span key={c.p} title={BIM_PHASE_LABELS[c.p] + ' criteria checked'} style={{
                fontFamily: '"DM Mono",monospace', fontSize: 9.5, flexShrink: 0,
                color: c.complete ? '#1D9E75' : PHASE_COLORS[c.p],
              }}>{BIM_PHASE_LABELS[c.p]} {c.complete ? '✓' : c.done + '/' + c.total}</span>
            ))}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {!element.started && <ActionMenuItem onClick={handleMarkStarted}>Mark Started</ActionMenuItem>}
        {phasesWithCriteria.map(p => {
          const complete = !!element[p + '_complete']
          return (
            <ActionMenuItem key={p} onClick={() => togglePhaseComplete(systemId, element, p, !complete)}>
              {complete ? 'Mark ' + BIM_PHASE_LABELS[p] + ' Incomplete' : 'Mark ' + BIM_PHASE_LABELS[p] + ' Complete'}
            </ActionMenuItem>
          )
        })}
      </ActionMenu>
      {!isCollapsed && phasesWithCriteria.length > 0 && (
        <div className="wf-children">
          <BimElementChildren element={element} systemId={systemId} restrictPhase={restrictPhase} />
        </div>
      )}
    </div>
  )
}

async function togglePhaseComplete(systemId, element, phase, complete) {
  updateElementInCache(systemId, element.id, el => {
    el[phase + '_complete'] = complete
    el[phase + '_complete_at'] = complete ? new Date().toISOString() : null
    el[phase + '_complete_by_name'] = complete ? actorName() : null
  })
  try { await markBimPhaseComplete(element.id, phase, complete, actorName()) }
  catch (e) { console.error('markPhaseComplete failed', e) }
}

export function BimElementChildren({ element, systemId, restrictPhase }) {
  const allPhases = BIM_PHASES.filter(p => element.criteria?.[p]?.length)
  const phases = restrictPhase ? allPhases.filter(p => p === restrictPhase) : allPhases
  if (!phases.length) return <LoadingRow text="No criteria on this element's template" />
  return <>{phases.map(p => <BimPhaseSection key={p} element={element} systemId={systemId} phase={p} />)}</>
}

/* ── BimPhaseSection — one phase's criteria checklist ───────────────────────── */

function BimPhaseSection({ element, systemId, phase }) {
  const collapseKey = 'bimPhase:' + element.id + ':' + phase
  const isCollapsed = wf.collapsed[collapseKey] !== false
  const menu = useActionMenu()
  const list = element.criteria[phase] || []
  const done = list.filter(c => c.done).length
  const complete = !!element[phase + '_complete']
  const label = BIM_PHASE_LABELS[phase] + ' Criteria'
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[collapseKey] = !isCollapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function exitSelectMode() { setSelectMode(false); setSelectedIds(new Set()) }

  function handleZoom() { ctx.zoomToSection('bim-phase-detail:' + element.id + ':' + phase, { root: 'design' }) }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}${complete ? ' wf-node--is-done' : ''}`} data-key={collapseKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on this phase" data-kbzoom="1" onClick={handleZoom} style={{ background: 'var(--wf-dot)' }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          <span className="wf-prog" style={{ color: done >= list.length ? '#1D9E75' : 'var(--aj-text-muted)', marginLeft: 8 }}>{done}/{list.length}</span>
          {complete && (
            <span style={{
              fontFamily: '"DM Mono",monospace', fontSize: 9, textTransform: 'uppercase', flexShrink: 0,
              color: '#1D9E75', border: '0.5px solid rgba(29,158,117,0.35)', borderRadius: 20, padding: '1px 7px',
            }} title={element[phase + '_complete_by_name'] ? 'Completed by ' + element[phase + '_complete_by_name'] : ''}>
              {BIM_PHASE_LABELS[phase]} Complete{element[phase + '_complete_by_name'] ? ' · ' + element[phase + '_complete_by_name'] : ''}
            </span>
          )}
          {element.videoTutorialUrls?.[phase] && (
            <a href={element.videoTutorialUrls[phase]} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              title="Watch video tutorial" style={{
                fontFamily: '"DM Mono",monospace', fontSize: 9, textTransform: 'uppercase', flexShrink: 0,
                color: '#1a6fa8', border: '0.5px solid rgba(26,111,168,0.35)', borderRadius: 20, padding: '1px 7px', textDecoration: 'none',
              }}>▶ Video</a>
          )}
          {element.visualAids?.[phase]?.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }}>
              {element.visualAids[phase].map(url => (
                <a key={url} href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="View visual aid">
                  <img src={url} alt="" style={{ width: 18, height: 18, objectFit: 'cover', borderRadius: 3, border: '0.5px solid var(--aj-border-mid)', display: 'block' }} />
                </a>
              ))}
            </span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => togglePhaseComplete(systemId, element, phase, !complete)}>
          {complete ? 'Mark ' + BIM_PHASE_LABELS[phase] + ' Incomplete' : 'Mark ' + BIM_PHASE_LABELS[phase] + ' Complete'}
        </ActionMenuItem>
        {list.length > 0 && <ActionMenuDivider />}
        {list.length > 0 && (
          <ActionMenuItem onClick={() => setSelectMode(true)}>Select criteria</ActionMenuItem>
        )}
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {list.map(c => (
            <BimCriterionRow
              key={c.id} criterion={c} element={element} systemId={systemId} phase={phase}
              selectMode={selectMode} isSelected={selectedIds.has(c.id)} onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}
      {selectMode && (
        <BimClFloatBar
          selectedIds={selectedIds} list={list} elementId={element.id} systemId={systemId} phase={phase}
          onDone={exitSelectMode} onCancel={exitSelectMode}
        />
      )}
    </div>
  )
}

/* ── BimClFloatBar — bulk check/uncheck for a phase's selected criteria ───────── */

function BimClFloatBar({ selectedIds, list, elementId, systemId, phase, onDone, onCancel }) {
  const [saving, setSaving] = useState(false)
  const sel = list.filter(c => selectedIds.has(c.id))

  async function bulkSet(done) {
    if (saving || !sel.length) return
    setSaving(true)
    const name = actorName()
    updateElementInCache(systemId, elementId, el => {
      el.criteria = { ...el.criteria, [phase]: el.criteria[phase].map(c =>
        selectedIds.has(c.id) ? { ...c, done, by_name: done ? name : null, at: done ? new Date().toISOString() : null } : c) }
    })
    try { await Promise.all(sel.map(c => toggleBimCriterion(elementId, c.id, done, name))) }
    catch (err) { console.error('[BimClFloatBar] bulk toggle failed', err) }
    finally { setSaving(false); onDone() }
  }

  return (
    <div className="wf-sheet-cl-float">
      <span style={{ flex: 1 }}>{sel.length} selected</span>
      <button
        onClick={() => bulkSet(true)}
        disabled={saving || sel.length === 0}
        style={{
          background: sel.length > 0 ? '#1D9E75' : '#3a5a68', border: 'none', borderRadius: 4,
          color: '#fff', padding: '5px 14px', fontSize: 12, cursor: sel.length > 0 ? 'pointer' : 'not-allowed',
        }}
      >{saving ? '…' : 'Check'}</button>
      <button
        onClick={() => bulkSet(false)}
        disabled={saving || sel.length === 0}
        style={{
          background: sel.length > 0 ? '#5a6a70' : '#3a5a68', border: 'none', borderRadius: 4,
          color: '#fff', padding: '5px 14px', fontSize: 12, cursor: sel.length > 0 ? 'pointer' : 'not-allowed',
        }}
      >{saving ? '…' : 'Uncheck'}</button>
      <button
        onClick={onCancel}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff9999', padding: 0, fontSize: 11 }}
      >✕ Done</button>
    </div>
  )
}

/* ── BimCriterionRow — leaf checklist row ───────────────────────────────────── */

function BimCriterionRow({ criterion, element, systemId, phase, selectMode, isSelected, onToggleSelect }) {
  const dataKey = 'bimCriterion:' + element.id + ':' + criterion.id
  const menu = useActionMenu()

  function handleToggle() { toggleBimCriterionState(systemId, element.id, phase, criterion.id) }

  return (
    <div
      className={'wf-node wf-node--item' + (criterion.done ? ' wf-node--is-done' : '')} data-key={dataKey}
      data-cl-kind="bim-criterion" data-cl-item={criterion.id}
      data-cl-elementid={element.id} data-cl-systemid={systemId} data-cl-phase={phase}
    >
      <div className="wf-row">
        {selectMode ? (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        ) : (
          <ActionMenuButton menu={menu} triangle />
        )}
        {selectMode ? (
          <input
            type="checkbox" className="wf-item-cb" checked={isSelected}
            onChange={() => onToggleSelect && onToggleSelect(criterion.id)}
            onClick={e => e.stopPropagation()}
            style={{ accentColor: '#1D9E75' }}
          />
        ) : (
          // Criterion text can wrap to multiple lines (unlike short checklist item names) — pin
          // the dot to the TOP of its grid cell and nudge it down by half the gap between one
          // line's height and the dot's own height, so it centers against the first line instead
          // of the whole multi-line block. Row's own `align-items: center` would otherwise center
          // it against the tallest item in the row (the wrapped text).
          <span className="wf-bullet wf-bullet--item" style={{ alignSelf: 'flex-start', marginTop: 4.8 }}></span>
        )}
        <div className="wf-text-wrap">
          {criterion.kind === 'coord' && (
            <span style={{
              fontFamily: '"DM Mono",monospace', fontSize: 8.5, textTransform: 'uppercase', flexShrink: 0,
              color: '#b8540a', border: '0.5px solid rgba(184,84,10,0.35)', borderRadius: 20, padding: '1px 6px',
            }} title="Coordinate with consultants / design partners">COORD</span>
          )}
          <span className="wf-node-text wf-node-text--item wf-criterion-text" style={{
            flex: 1, minWidth: 0,
            textDecoration: criterion.done ? 'line-through' : 'none',
            opacity: criterion.done ? 0.6 : 1,
            whiteSpace: 'normal',
          }}>
            <Markdown text={criterion.text} />
          </span>
          <span className="wf-meta">
            {criterion.done && (
              <span className="wf-badge wf-badge--green" title={criterion.at ? criterion.at.slice(0, 10) : undefined}>
                {criterion.by_name || 'Done'}
              </span>
            )}
          </span>
        </div>
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleToggle}>{criterion.done ? 'Mark incomplete' : 'Complete'}</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── Entity-zoom renders (children only — ZoomTitle owns the header) ─────────── */

export function BimSystemZoom({ systemId }) {
  return (
    <div className="wf-list">
      <BimSystemChildren systemId={systemId} />
    </div>
  )
}

/** Deep-linkable element resolution — shared by BimElementZoom and BimPhaseZoom (one level
 *  deeper: System → Element → Phase). Prefers the freshest copy (the system's loader cache,
 *  where mutations land first), falling back to the by-id zoom cache for a cold deep link. */
function useResolvedBimElement(elementId) {
  useEffect(() => {
    if (ctx.bimZoomCache?.[elementId]) return
    ctx.loader.load('bimElementOne:' + elementId, () => getBimElement(elementId).then(d => {
      if (!ctx.bimZoomCache) ctx.bimZoomCache = {}
      ctx.bimZoomCache[elementId] = { element: d.element, system: d.system }
      if (!ctx.bimElementSystem) ctx.bimElementSystem = {}
      if (d.system) ctx.bimElementSystem[elementId] = d.system.id
      return [d]
    }))
  }, [elementId])

  const systemId = ctx.bimElementSystem?.[elementId] || ctx.bimZoomCache?.[elementId]?.system?.id || null
  let element = null
  if (systemId) {
    const els = ctx.loader.get(loaderKey(systemId))
    if (Array.isArray(els)) element = els.find(e => e.id === elementId) || null
  }
  if (!element) element = ctx.bimZoomCache?.[elementId]?.element || null
  return { element, systemId }
}

export function BimElementZoom({ elementId }) {
  const { element, systemId } = useResolvedBimElement(elementId)
  if (!element) return <div className="wf-list"><LoadingRow text="Loading element..." /></div>
  return (
    <div className="wf-list">
      <BimElementChildren element={element} systemId={systemId || element.system_id} />
    </div>
  )
}

export function BimPhaseZoom({ elementId, phase }) {
  const { element, systemId } = useResolvedBimElement(elementId)
  if (!element) return <div className="wf-list"><LoadingRow text="Loading criteria..." /></div>
  const list = element.criteria?.[phase] || []
  const sysId = systemId || element.system_id
  return (
    <div className="wf-list">
      {list.map(c => <BimCriterionRow key={c.id} criterion={c} element={element} systemId={sysId} phase={phase} />)}
    </div>
  )
}
